"use client"

import { useState } from "react"
import { useParams, usePathname } from "next/navigation"
import { Header } from "@/components/header"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Separator } from "@/components/ui/separator"
import {
  Heart,
  ShoppingCart,
  Star,
  Download,
  Share2,
  Flag,
  ThumbsUp,
  MessageCircle,
  Calendar,
  Eye,
  Award,
} from "lucide-react"
import type { PromptDetail } from "@/lib/mock-data"
import { useAppState } from "@/lib/app-state"
import Link from "next/link"
import { getLocaleFromPathname, hrefWithLocale } from "@/lib/i18n"

type Props = {
  initialSummary: PromptDetail | null
  initialDetail: PromptDetail | null
  loadError?: string | null
}

export default function PromptDetailClient({ initialSummary, initialDetail, loadError }: Props) {
  const pathname = usePathname()
  const locale = getLocaleFromPathname(pathname)
  const params = useParams()
  const { favorites, purchased, addToCart, toggleFavorite } = useAppState()
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [actionMessage, setActionMessage] = useState("")
  const promptId = typeof params.id === "string" ? params.id : ""

  const promptSummary = initialSummary
  const promptDetail = initialDetail
  const isFavorited = favorites.includes(promptId)
  const isPurchased = purchased.includes(promptId)

  const handleAddToCart = () => {
    addToCart(promptId)
    setActionMessage("장바구니에 담겼습니다.")
  }
  const handleToggleFavorite = () => toggleFavorite(promptId)

  const handleCopyPrompt = async () => {
    if (!promptDetail) return
    await navigator.clipboard.writeText(promptDetail.longDescription)
    setActionMessage("프롬프트 내용을 클립보드에 복사했습니다.")
  }

  const handleDownloadFile = () => {
    if (!promptDetail) return
    const fileBlob = new Blob([promptDetail.longDescription], { type: "text/plain;charset=utf-8" })
    const url = URL.createObjectURL(fileBlob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${promptDetail.title}.txt`
    link.click()
    URL.revokeObjectURL(url)
    setActionMessage("프롬프트 파일 다운로드를 시작했습니다.")
  }

  if (loadError && loadError !== "not_found") {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">불러오기 오류</h1>
          <p className="text-muted-foreground mb-8">{loadError}</p>
          <Button asChild>
            <Link href={hrefWithLocale(locale, "/prompts")}>목록으로 돌아가기</Link>
          </Button>
        </div>
      </div>
    )
  }

  if (!promptSummary || !promptDetail) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold mb-4">프롬프트를 찾을 수 없습니다</h1>
          <p className="text-muted-foreground mb-8">
            요청하신 ID({promptId || "없음"})에 해당하는 프롬프트가 존재하지 않거나 아직 게시되지 않았습니다.
          </p>
          <Button asChild>
            <Link href={hrefWithLocale(locale, "/prompts")}>목록으로 돌아가기</Link>
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <div className="mb-6">
              <div className="aspect-video bg-muted rounded-lg overflow-hidden mb-4">
                <img
                  src={promptDetail.images[selectedImageIndex] || "/placeholder.svg"}
                  alt={promptDetail.title}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex space-x-2">
                {promptDetail.images.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setSelectedImageIndex(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 ${
                      selectedImageIndex === index ? "border-primary" : "border-transparent"
                    }`}
                  >
                    <img
                      src={image || "/placeholder.svg"}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>

            <Tabs defaultValue="description" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="description">상세 설명</TabsTrigger>
                <TabsTrigger value="reviews">리뷰 ({promptDetail.reviewCount})</TabsTrigger>
                <TabsTrigger value="author">판매자 정보</TabsTrigger>
              </TabsList>

              <TabsContent value="description" className="mt-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="prose max-w-none">
                      <p className="text-muted-foreground mb-4">{promptDetail.description}</p>
                      <div className="whitespace-pre-line">{promptDetail.longDescription}</div>

                      <div className="mt-6">
                        <h4 className="font-semibold mb-3">주요 특징</h4>
                        <ul className="space-y-2">
                          {promptDetail.features.map((feature, index) => (
                            <li key={index} className="flex items-center">
                              <Award className="h-4 w-4 text-primary mr-2" />
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="mt-6">
                        <h4 className="font-semibold mb-3">태그</h4>
                        <div className="flex flex-wrap gap-2">
                          {promptDetail.tags?.map((tag) => (
                            <Badge key={tag} variant="outline">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="reviews" className="mt-6">
                <div className="space-y-4">
                  {promptDetail.reviews.length === 0 ? (
                    <Card>
                      <CardContent className="p-6 text-sm text-muted-foreground">아직 등록된 리뷰가 없습니다.</CardContent>
                    </Card>
                  ) : (
                    promptDetail.reviews.map((review) => (
                      <Card key={review.id}>
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback>{review.author[0]}</AvatarFallback>
                              </Avatar>
                              <div>
                                <div className="font-medium">{review.author}</div>
                                <div className="text-sm text-muted-foreground">{review.date}</div>
                              </div>
                            </div>
                            <div className="flex items-center">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/50"
                                  }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm mb-3">{review.content}</p>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <button type="button" className="flex items-center space-x-1 hover:text-foreground">
                              <ThumbsUp className="h-3 w-3" />
                              <span>도움됨 ({review.helpful})</span>
                            </button>
                            <button type="button" className="flex items-center space-x-1 hover:text-foreground">
                              <MessageCircle className="h-3 w-3" />
                              <span>답글</span>
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              <TabsContent value="author" className="mt-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-start space-x-4">
                      <Avatar className="h-16 w-16">
                        <AvatarImage src={promptDetail.authorProfile.avatar || "/placeholder.svg"} />
                        <AvatarFallback>{promptDetail.authorProfile.name[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{promptDetail.authorProfile.name}</h3>
                        <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-3">
                          <div className="flex items-center">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 mr-1" />
                            {promptDetail.authorProfile.rating}
                          </div>
                          <div>{promptDetail.authorProfile.promptCount}개 프롬프트</div>
                          <div>{promptDetail.authorProfile.totalSales.toLocaleString()}회 판매</div>
                        </div>
                        <div className="flex items-center text-sm text-muted-foreground mb-4">
                          <Calendar className="h-4 w-4 mr-1" />
                          {promptDetail.authorProfile.joinDate} 가입
                        </div>
                        <Button variant="outline" size="sm" type="button">
                          판매자 프로필 보기
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <Badge variant="secondary">{promptDetail.category}</Badge>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      type="button"
                      onClick={handleToggleFavorite}
                      className={isFavorited ? "text-red-500" : ""}
                    >
                      <Heart className={`h-4 w-4 ${isFavorited ? "fill-current" : ""}`} />
                    </Button>
                    <Button variant="ghost" size="icon" type="button">
                      <Share2 className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" type="button">
                      <Flag className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <CardTitle className="text-xl">{promptDetail.title}</CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                {actionMessage && <p className="text-xs text-green-600">{actionMessage}</p>}
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center space-x-1">
                    <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    <span>{promptDetail.rating}</span>
                    <span className="text-muted-foreground">({promptDetail.reviewCount})</span>
                  </div>
                  <div className="flex items-center space-x-4 text-muted-foreground">
                    <div className="flex items-center space-x-1">
                      <Download className="h-3 w-3" />
                      <span>{promptDetail.downloadCount.toLocaleString()}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Eye className="h-3 w-3" />
                      <span>{promptDetail.viewCount.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <Separator />

                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-2xl font-bold">₩{promptDetail.price.toLocaleString()}</span>
                    {promptDetail.originalPrice && (
                      <span className="text-lg text-muted-foreground line-through">
                        ₩{promptDetail.originalPrice.toLocaleString()}
                      </span>
                    )}
                  </div>
                  {promptDetail.originalPrice ? (
                    <Badge variant="destructive" className="text-xs">
                      {Math.round((1 - promptDetail.price / promptDetail.originalPrice) * 100)}% 할인
                    </Badge>
                  ) : null}
                </div>

                <Separator />

                <div className="space-y-3">
                  {isPurchased ? (
                    <>
                      <Button className="w-full" size="lg" type="button" onClick={handleCopyPrompt}>
                        프롬프트 복사
                      </Button>
                      <Button variant="outline" className="w-full bg-transparent" size="lg" type="button" onClick={handleDownloadFile}>
                        <Download className="mr-2 h-4 w-4" />
                        파일 다운로드
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button className="w-full" size="lg" asChild>
                        <Link href={hrefWithLocale(locale, `/checkout?promptId=${encodeURIComponent(promptId)}`)}>
                          <Download className="mr-2 h-4 w-4" />
                          바로 구매하기
                        </Link>
                      </Button>
                      <Button variant="outline" className="w-full bg-transparent" size="lg" type="button" onClick={handleAddToCart}>
                        <ShoppingCart className="mr-2 h-4 w-4" />
                        장바구니 담기
                      </Button>
                    </>
                  )}
                </div>

                <Separator />

                <div className="space-y-2">
                  <h4 className="font-medium">포함 내용</h4>
                  <ul className="text-sm space-y-1">
                    {promptDetail.features.slice(0, 3).map((feature, index) => (
                      <li key={index} className="flex items-center text-muted-foreground">
                        <Award className="h-3 w-3 text-primary mr-2 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  )
}
